import { Box, Container, HStack, VStack, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GlobalAudioPlayer } from '../player/GlobalAudioPlayer';
import { FullPlayer } from '../player/FullPlayer';
import { useAuth } from '../auth/AuthProvider';
import { UserPopup, useIsPaidSub } from '../user/UserPopup';
import { RootState } from '../redux/store';
import { MicRecorderModal } from '../library/MicRecorderModal';
import { addTracks, setReferenceId } from '../redux/features/localAudioSlice';
import { PaywallModal } from '../paywall/PaywallModal';
import { useGetCreditsBalanceQuery } from '../redux/services/mixgenie';
import { MG_GRADIENT, MG_GRADIENT_FLAT } from '../theme';

// Shell for the /songs and /credits pages (the ones that still
// live outside the standalone composer / studio surfaces). Visual
// language matches ComposerPage: light semantic tokens (mg.bg / mg.fg),
// serif "mix GENIE" wordmark, clickable credits chip that opens the
// shared PaywallModal. Replaces the former dark-gradient AppShell that
// pre-dated the composer cutover and clashed with everything else.

type NavTab =
  | { to: string; label: string; icon: string; matches: RegExp }
  | { action: 'record'; label: string; icon: string; matches?: undefined };

const TABS: NavTab[] = [
  // "Create" now points at /composer (post-cutover). /generator still
  // works as a legacy bookmark but shouldn't be the primary nav target.
  { to: '/composer',   label: 'Create',    icon: '✦', matches: /^\/composer(\/|$)/ },
  { to: '/songs',      label: 'Songs',     icon: '♫', matches: /^\/(songs|history)(\/|$)/ },
  { action: 'record',  label: 'Record',    icon: '🎙' },
];

const NAV_HEIGHT = 68;
const HEADER_HEIGHT = 56;

export function AppShell({ children }: { children: React.ReactNode }) {
  const { authenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const hasQueue = useSelector((s: RootState) => s.player.index >= 0);
  const micRecorder = useDisclosure();
  const paywall = useDisclosure();
  const userPopup = useDisclosure();
  const isAuthed = !!authenticated || !!user?.email;
  // Credits chip routing — identical to the composer's: paying users
  // land on UserPopup (balance + Manage billing + Get more credits +
  // Log out); free users land on the paywall directly. See
  // src/user/UserPopup.tsx for the useIsPaidSub signal.
  const isPaidSub = useIsPaidSub(isAuthed);
  const openAccountSurface = () => {
    if (isPaidSub) userPopup.onOpen();
    else paywall.onOpen();
  };

  // Credits chip balance — identical read path to the composer.
  // normalizeCredits returns `{data: {creditsRemaining, currentPlan,
  // purchased, ...}}`; the `.balance` field doesn't exist (that was the
  // old bug that left both pills on "…" forever).
  const { data: creditsData } = useGetCreditsBalanceQuery(undefined as any, { skip: !isAuthed });
  const credits = (() => {
    if (!isAuthed || !creditsData) return null;
    const d = (creditsData as any).data ?? creditsData;
    const total = d?.creditsRemaining ?? ((d?.currentPlan || 0) + (d?.purchased || 0));
    return Number.isFinite(total) ? Number(total) : null;
  })();

  return (
    <Box minH="100vh" bg="mg.bg" color="mg.fg" position="relative">
      <Box
        as="header"
        position="sticky"
        top={0}
        zIndex={20}
        h={`${HEADER_HEIGHT}px`}
        bg="mg.surface"
        borderBottom="1px solid"
        borderColor="mg.line"
      >
        <Container maxW="1120px" h="full" px={{ base: 3, md: 6 }}>
          <HStack h="full" justify="space-between" align="center">
            <Link to="/composer" aria-label={t('nav.home', 'MixGenie home')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Box as="img" src="/logo/logo_single.png" alt="" w="28px" h="28px" flex="0 0 auto" />
              <Text
                fontWeight={500}
                fontSize="22px"
                letterSpacing="-0.02em"
                color="mg.fg"
              >
                mix
                <Box
                  as="em"
                  fontStyle="normal"
                  fontWeight={700}
                  bgGradient={MG_GRADIENT_FLAT}
                  bgClip="text"
                  sx={{ WebkitBackgroundClip: 'text', color: 'transparent' }}
                >
                  GENIE
                </Box>
              </Text>
            </Link>
            <HStack spacing={2}>
              {isAuthed ? (
                // Mirrors the composer TopBar: when the balance is at
                // or below zero, pill becomes a filled gradient CTA
                // reading "Get more credits" so the user never sees a
                // literal "−1 credits" label. Null credits (still
                // loading) → neutral chip with "…".
                (credits !== null && credits <= 0) ? (
                  <Box
                    as="button"
                    display="inline-flex"
                    alignItems="center"
                    gap={1.5}
                    bgGradient={MG_GRADIENT}
                    color="white"
                    fontSize="11.5px"
                    fontWeight={800}
                    letterSpacing="0.01em"
                    px={3}
                    py={1.5}
                    borderRadius="full"
                    border={0}
                    cursor="pointer"
                    onClick={openAccountSurface}
                    _hover={{ opacity: 0.92 }}
                    boxShadow="0 6px 14px -6px rgba(236,72,153,0.55)"
                    aria-label="Get more credits"
                  >
                    <Box as="span" fontSize="12px">⚡</Box>
                    <Text as="span">Get more credits</Text>
                  </Box>
                ) : (
                  <HStack
                    as="button"
                    spacing={1.5}
                    bg="mg.surfaceHi"
                    color="mg.muted"
                    fontSize="11.5px"
                    fontWeight={700}
                    px={2.5}
                    py={1.5}
                    borderRadius="full"
                    border="1px solid"
                    borderColor="mg.line"
                    cursor="pointer"
                    onClick={openAccountSurface}
                    _hover={{ bg: 'mg.surface', color: 'mg.fg' }}
                    aria-label={isPaidSub ? 'Account' : 'Get more credits'}
                  >
                    <Box w="6px" h="6px" borderRadius="full" bgGradient={MG_GRADIENT} />
                    <Text as="span">{credits ?? '…'} credits</Text>
                  </HStack>
                )
              ) : (
                <Box
                  as={Link}
                  to="/login"
                  fontSize="12px"
                  fontWeight={700}
                  color="mg.fg"
                  px={3}
                  py={2}
                  borderRadius="full"
                  bg="mg.surfaceHi"
                  border="1px solid"
                  borderColor="mg.line2"
                  _hover={{ bg: 'mg.surface', textDecoration: 'none' }}
                >
                  {t('nav.signIn', 'Sign in')}
                </Box>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Box
        as="main"
        position="relative"
        zIndex={1}
        pb={{
          base: `calc(${NAV_HEIGHT + (hasQueue ? 72 : 0)}px + env(safe-area-inset-bottom, 0px) + 16px)`,
        }}
      >
        <Container maxW="1120px" px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
          {children}
        </Container>
      </Box>

      <Box
        as="nav"
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        zIndex={15}
        bg="mg.surface"
        borderTop="1px solid"
        borderColor="mg.line"
        pb="env(safe-area-inset-bottom, 0px)"
        display={isAuthed ? 'block' : 'none'}
        aria-label={t('nav.appNav', 'App navigation')}
      >
        <HStack
          maxW="640px"
          mx="auto"
          h={`${NAV_HEIGHT}px`}
          px={2}
          spacing={0}
          align="stretch"
          justify="space-between"
        >
          {TABS.map((tab) => {
            const isActive = 'matches' in tab && tab.matches ? tab.matches.test(location.pathname) : false;
            const key = 'to' in tab ? tab.to : `action:${tab.action}`;
            const inner = (
              <VStack
                h="full"
                justify="center"
                spacing={1}
                py={2}
                transition="all 0.15s ease"
                color={isActive ? 'hot.500' : 'mg.muted'}
                _hover={{ color: 'mg.fg' }}
                position="relative"
              >
                {isActive && (
                  <Box
                    position="absolute"
                    top={0}
                    left="50%"
                    transform="translateX(-50%)"
                    w="26px"
                    h="2px"
                    borderRadius="0 0 6px 6px"
                    bgGradient={MG_GRADIENT_FLAT}
                  />
                )}
                <Text fontSize="20px" lineHeight="1" aria-hidden>{tab.icon}</Text>
                <Text
                  fontSize="10.5px"
                  fontWeight={isActive ? 700 : 500}
                  letterSpacing="0.02em"
                >
                  {tab.label}
                </Text>
              </VStack>
            );
            if ('to' in tab) {
              return (
                <Link
                  key={key}
                  to={tab.to}
                  aria-current={isActive ? 'page' : undefined}
                  data-cta={`nav:${tab.label.toLowerCase()}`}
                  style={{ flex: 1, textDecoration: 'none' }}
                >
                  {inner}
                </Link>
              );
            }
            return (
              <Box
                key={key}
                as="button"
                onClick={micRecorder.onOpen}
                data-cta={`nav:${tab.label.toLowerCase()}`}
                aria-label="Record with mic"
                style={{ flex: 1, background: 'transparent', border: 0, cursor: 'pointer' }}
              >
                {inner}
              </Box>
            );
          })}
        </HStack>
      </Box>

      <GlobalAudioPlayer />
      <FullPlayer />

      {/* Mic recorder — bottom-tab Record action opens this from any page. */}
      <MicRecorderModal
        isOpen={micRecorder.isOpen}
        onCancel={micRecorder.onClose}
        onConfirm={(file) => {
          const id = `mic-${Date.now()}`;
          dispatch(addTracks([{
            id,
            file,
            name: file.name,
            size: file.size,
            addedAt: Date.now(),
          }]));
          dispatch(setReferenceId(id));
          micRecorder.onClose();
          toast({
            title: t('nav.micAttached', 'Recording attached as reference'),
            status: 'success',
            duration: 2000,
          });
          navigate('/composer');
        }}
      />

      {/* Shared paywall sheet — opens from the credits chip for free
          users, and from the UserPopup's "Get more credits" button for
          paying users. Same component the composer uses on 402 /
          INSUFFICIENT_CREDITS. */}
      <PaywallModal
        isOpen={paywall.isOpen}
        onClose={paywall.onClose}
        reason="credits-chip"
      />

      {/* Account sheet for paying users — opens from the credits chip
          when the user has an active paid subscription. */}
      <UserPopup
        isOpen={userPopup.isOpen}
        onClose={userPopup.onClose}
        email={user?.email || ''}
        onGetMoreCredits={paywall.onOpen}
        onSignOut={async () => { await logout(); navigate('/composer'); }}
      />
    </Box>
  );
}
